import Loader from "./Loader";

/**
 * Paginator che carica post data la route di una api al click di un button.
 * @param {string} apiToCall stringa contenente l'url da fetchare.
 * @param {Array} currentPosts array di post che contiene tutti quelli caricati.
 * @param {boolean} fetchData bool per capire se fare una chiamata al server o se utilizzare currentPosts.
 * @param {Number} numberOfPostsPerPage numero di post da fetchare.
 * @param {Number} lastIndex ultimo indice degli elementi caricati.
 * @param {string} wrapperClass classe del container in cui andranno le card.
 * @param {string} template nome del template da utilizzare.
 */
export default class Paginator {
  constructor(
    apiToCall = null,
    currentPosts = null,
    dataFromServer = null,
    fetchData = false,
    numberOfPostsPerPage = 0
  ) {
    this.apiToCall = apiToCall;
    this.currentPosts = currentPosts ?? [];
    this.dataFromServer = dataFromServer;
    this.fetchData = fetchData;
    this.numberOfPostsPerPage = Number.parseInt(numberOfPostsPerPage);
    this.lastIndex = 0;
    this.wrapperClass = null;
    this.template = null;
    this.loader = new Loader({ minimumTime: 1000, cssClass: ".js-loader" });
  }

  async init() {
    const btnLoadMore = document.querySelector(".js-btn-load-more");
    // Se non esiste il button non faccio nulla
    if (!btnLoadMore) {
      console.warn("Button Load more not found", btnLoadMore);
      return;
    }

    if (this.fetchData) {
      await this._fetchData();
    }

    if (!this.dataFromServer || this.dataFromServer.length == 0) {
      btnLoadMore.style.display = "none";
      return;
    }

    // EventListener sul click
    btnLoadMore.addEventListener("click", this.onClickLoadMore.bind(this));
  }

  _emptyContainer() {
    const container = document.querySelector(this.wrapperClass);
    const childrenFromContainer = container.children;

    const childrenFromContainerCount = Array.from(childrenFromContainer).length;
    const childrenToRemove = [];

    for (let i = 0; i < childrenFromContainerCount; i++) {
      const item = childrenFromContainer[i];

      if (item.id !== `card-${this.template}-template`) {
        childrenToRemove.push(item);
      }
    }

    for (let i = childrenToRemove.length - 1; i >= 0; i--) {
      const item = childrenToRemove[i];
      container.removeChild(item);
    }
  }

  /**
   * onClick event handler di "Load more", una volta premuto fetcha i prossimi n post.
   */
  async onClickLoadMore() {
    this.loader.toggle(true);
    //se non viene impostata la route dell'api e non ci sono altri dati in pagina, si ferma
    if (!this.apiToCall && this.fetchData) {
      this.loader.toggle(false);
      return;
    }

    if (this.fetchData) {
      await this._fetchData();      
    }

    this._renderBlocks();

    this.lastIndex += this.numberOfPostsPerPage;
    if (this.lastIndex >= this.dataFromServer.length) {
      const btnLoadMore = document.querySelector(".js-btn-load-more");
      btnLoadMore.style.display = "none";
    }
    this.loader.toggle(false);
  }

  /**
   * _fetchData prende i dati dal server.
   */
  async _fetchData() {
    const request = await fetch(this.apiToCall, {
      method: "GET",
    });

    const json = await request.json();
    if (!json) {
      return;
    }

    this.dataFromServer = this._parseDataFromApi(json);
  }

  /**
   *  Crea i blocchi necessari con i dati che arrivano dal server.
   */
  _renderBlocks() {
    if (!this.dataFromServer || this.dataFromServer.length == 0) {
      console.warn("No results found", this.dataFromServer);
      return;
    }
    if (!this.wrapperClass) {
      return;
    }
    //Trovo il container delle card
    const container = document.querySelector(this.wrapperClass);
    if (Array.isArray(this.dataFromServer)) {
      let numberOfIterations = 0;
      const templateCard = document.querySelector(
        `.library>#card-${this.template}-template`
      );
      if (!templateCard) {
        return;
      }
      //Carico n card, dove n è il numero di post scelto (numberOfPostsPerPage)
      for (
        let i = this.lastIndex;
        numberOfIterations < this.numberOfPostsPerPage &&
        i < this.dataFromServer.length;
        i++, numberOfIterations++
      ) {
        //Clono il template della card e mi ricavo i blocchi da modificare
        let item = this.dataFromServer[i];
        const duplicatedCard = templateCard.cloneNode(true);
        duplicatedCard.href = item.permalink;
        const InfoContainer = duplicatedCard.querySelector(
          `.${this.template}-text`
        );
        const Date = InfoContainer.querySelector(`.${this.template}-date>span`);
        const Title = InfoContainer.querySelector(`.${this.template}-title`);
        const Description = InfoContainer.querySelector(
          `.${this.template}-desc`
        );
        //Se i dati arrivano direttamente dal server, creo un frammento html e mi ricavo l'src, invece, se i dati vengono ricavati da fetch,
        //prendo il link della source dell'immagine
        let FeaturedImage = duplicatedCard.querySelector(".featured-image");
        if (!this.fetchData) {
          let img = document
            .createRange()
            .createContextualFragment(item.thumbnail).firstChild;

          if (img && FeaturedImage) {
            FeaturedImage.src = img?.src;
          }
        } else if (item.thumbnail != null) {
          FeaturedImage.src = item.thumbnail;
        }

        //sostituisco i valori
        if (Date) {
          Date.innerText = item.date;
        }
        if (Title) {
          Title.innerText = item.title;
        }
        if (Description) {
          Description.innerText = item.excerpt;
        }
        this.currentPosts.push(item);
        duplicatedCard.id = "";
        container.appendChild(duplicatedCard);
      }

      const lastChild = container.lastChild;
      const header = document.querySelector('.js-site-header');
      const timeout = setTimeout(() => {
        window.scrollTo({
          top: lastChild.offsetTop - header.offsetHeight - 10,
          behavior: "smooth",
        });
        clearTimeout(timeout);
      }, 10);
    }
  }

  _ellipseText(text, maxCharacters = 50) {
    if (!text) {
      return "";
    }

    if (text.length > maxCharacters) {
      return `${text.substr(0, maxCharacters)}...`;
    }

    return text;
  }

  /**
   * Converte la struttura di default di wordpress in un oggetto più semplice.
   * @param {object} data
   * @returns Un array di oggetti convertiti o null.
   */
  _parseDataFromApi(data) {
    if (!data) {
      return null;
    }

    const parsedData = [];

    if (Array.isArray(data)) {
      for (let item of data) {
        const parsedObject = {
          title: item.title.rendered,
          thumbnail:
            item._embedded["wp:featuredmedia"]?.[0]?.source_url ?? null,
          date: new Date(item.date)
            .toLocaleDateString("en-us", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
            .replace(",", ""),
          description: item.excerpt,
          permalink: item.link,
        };

        parsedData.push(parsedObject);
      }
    }

    return parsedData;
  }
}
